<template>
  <div class="chat-audio">
    <button
      class="play-btn"
      type="button"
      @click="togglePlay"
      :aria-label="ariaLabel"
      :aria-busy="isLoading"
    >
      <font-awesome-icon v-if="isLoading" icon="spinner" spin />
      <font-awesome-icon v-else :icon="isPlaying ? 'pause' : 'play'" />
    </button>
    <div
      class="waveform"
      ref="track"
      role="slider"
      tabindex="0"
      :aria-label="$t('audio.seek')"
      aria-valuemin="0"
      :aria-valuemax="Math.round(displayDuration) || 0"
      :aria-valuenow="Math.round(displayCurrentTime) || 0"
      :aria-valuetext="valueText"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @keydown="onKeydown"
    >
      <span
        v-for="(barHeight, index) in barHeights"
        :key="index"
        class="bar"
        :class="{ played: index < playedBars }"
        :style="{ height: barHeight + 'px' }"
      ></span>
    </div>
    <span v-if="timeLabel" class="time">{{ timeLabel }}</span>
    <!-- The replay button, on a pill whose message carries span timings while
         phraseReplay is on. It restarts the span the learner is on, or the
         last span once the reply has finished; a quick second tap steps one
         span further back. Hidden below 290px of message width by the
         container query in TutorMessage's audio row. -->
    <button
      v-if="hasSpans"
      class="replay-btn"
      type="button"
      :aria-label="$t('audio.replaySpan')"
      :disabled="!spanNavigationOpen"
      @click="replaySpan"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M3 12a9 9 0 1 0 2.64-6.36" />
        <path d="M3 4v5h5" />
      </svg>
    </button>
    <!-- The speed chip. Only on a pill the chat marked with playbackControls
         (a tutor message in a lesson or a freestyle chat), only while
         audioPlaybackSpeed is on, and only once the platform can play at
         another rate. It names the speed it is at; a tap moves to the next. -->
    <button
      v-if="showSpeedChip"
      class="speed-chip"
      :class="{ 'speed-chip-slowed': chipRate !== 1 }"
      type="button"
      :aria-label="$t('audio.playbackSpeed', { speed: chipLabel })"
      @click="cycleSpeed"
    >
      {{ chipLabel }}
    </button>
    <!-- While a scrub snaps to span starts, the text of the span it would
         play from, above the pill. The slider's aria-valuetext carries the
         same text, so this copy is hidden from assistive technology. -->
    <span v-if="snapTooltip" class="scrub-tooltip" aria-hidden="true">{{ snapTooltip }}</span>
  </div>
</template>

<script>
import { toRaw } from "vue";
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { authStore } from "@/store/auth";
import { playbackState, play, pause, seek } from "@/scripts/audioPlaybackService";
import {
  choosePlaybackRate,
  effectivePlaybackRate,
  nextPlaybackRate,
  playbackRateAvailable,
  playbackRateState,
} from "@/scripts/playbackRate.js";
import { usePlaybackEvents } from "@/composables/usePlaybackEvents.js";
import {
  adoptClip,
  clipCacheGeneration,
  clipCacheState,
  getClip,
  holdClip,
  releaseClip,
} from "@/scripts/clipCache.js";
import {
  anySegmentSessionLive,
  cancelPendingPlaysOf,
  sliceState,
  supersedeOtherPendingPlays,
} from "@/scripts/slicePlayback.js";
import { recordingState } from "@/store/recordingState.js";

const BAR_COUNT = 16;
const KEYBOARD_SEEK_STEP_SECONDS = 5;
// The replay button's second tap steps one span further back only when it
// comes this soon after the first, and within that span's own duration, so a
// very short span replayed twice does not jump backwards.
const REPLAY_STEP_BACK_WINDOW_MS = 1500;
// A span counts as the one playing from a hair before its start, so a clock
// that sits exactly on a span's start is read as that span.
const SPAN_START_TOLERANCE_SECONDS = 0.001;
// When the player lets go of this clip within this much of its end, the reply
// counts as finished; earlier, it was stopped mid-clip (iOS drops its player
// when a phrase is tapped while a load crosses the bridge).
const NATURAL_END_TOLERANCE_SECONDS = 0.5;

// Counts every play a learner asks any pill for (a press, a scrub, a span
// replay or step) and every reply that autoplays, across all pills. A pill
// whose play is still waiting for its bytes gives it up when a newer request
// exists, so an older press can never take the player from a newer one on
// another pill.
let pillPlayRequests = 0;

export default {
  props: {
    audioId: String,
    isAutoPlay: Boolean,
    // Which message this clip belongs to. The shared playback service is keyed
    // by audioId, which is not a message, so the reply-lag audio stamp can only
    // identify the turn if the identity travels down with the player.
    dialogId: String,
    messageKey: String,
    // Set when a segment session already downloaded and played this reply.
    // The player then mounts paused and fetches nothing until the learner
    // presses play or scrubs: the bytes are the same audio they just heard,
    // and fetching them on intersection would double the reply's download.
    deferFetch: { type: Boolean, default: false },
    // Set only by TutorMessage for a tutor message in a lesson or a freestyle
    // chat. It shows the speed chip (while the flag and the platform allow it)
    // and makes this pill play, and label its times, at the chip's rate. The
    // learner's own recordings, the admin viewer and Daily Play never set it.
    playbackControls: { type: Boolean, default: false },
    // The message's span timings, handed over by TutorMessage only while
    // phraseReplay is on and the timings version is one this client knows.
    // With them the pill gains the replay button, keyboard seeking by span and
    // a scrub that snaps to span starts; without them it is today's pill.
    spanTimings: { type: Array, default: null },
  },
  data() {
    return {
      audioBlob: null,
      hasRequested: false,
      playWhenLoaded: this.isAutoPlay,
      observer: null,
      abortController: null,
      // Duration read from the blob's metadata so the pill can show a length
      // before the clip is ever played (playbackState only knows the current
      // clip). 0 until probed; stays 0 if the container reports Infinity.
      probedDuration: 0,
      isScrubbing: false,
      // Scrub position as a fraction of the clip, null when idle. A ratio needs
      // no duration, so the gesture works on a clip the player has never held.
      // It outlives pointerup deliberately: clearing on release would snap the
      // bars back to zero during the load. commitScrub owns clearing it.
      scrubRatio: null,
      scrubCommitId: 0,
      // Resolved by loadAudio on success, rejected on failure or user cancel,
      // so commitScrub has one thing to await instead of a set of exits.
      blobDeferred: null,
      // Releases the in-flight duration probe (see probeDuration); called on
      // settle or from beforeUnmount, whichever comes first.
      probeCleanup: null,
      // The clip this pill holds in the shared clip cache, which keeps the
      // cache from evicting it while the pill is mounted, or null. The id is
      // kept rather than a flag, so the hold released is always the one taken.
      heldClipId: null,
      // The clip cache's clear generation when this pill's bytes arrived. The
      // pill hands its bytes to the cache only if the cache has not been
      // cleared since, so a clear is never undone by a late fetch.
      blobCacheGeneration: null,
      // The audioId the bytes in audioBlob were fetched for.
      blobAudioId: null,
      // The phrase tap count (sliceState.taps) when the learner last asked
      // this pill to play, by a press, a scrub or an autoplay. A play that is
      // still waiting for its bytes is given up if a phrase was tapped since.
      playIntentTaps: sliceState.taps,
      // The pill play request count when the learner last asked this pill to
      // play (see pillPlayRequests). A reply that autoplays asks at mount.
      playIntentRequest:
        this.isAutoPlay && clipCacheState.enabled ? ++pillPlayRequests : pillPlayRequests,
      // While a scrub on a pill with timings is snapped, the index of the
      // span it would play from, which names the tooltip. Null otherwise.
      snapIndex: null,
      // The start, in media seconds, of the span a snapped drag would play
      // from, kept with the gesture so a release still knows it was snapped
      // after the timings went away mid-drag. Null for an unsnapped drag.
      snapStart: null,
      // The replay button's last tap, {at, index}, for the second-tap rule.
      lastReplay: null,
      // How many times span navigation has gone from allowed to refused (a
      // recording started, a reply began to be spoken as segments, the flag
      // went off). A span-navigation play that waited for its bytes starts
      // only if this did not move while it waited.
      spanGateClosures: 0,
      // How many span-navigation plays this pill has handed to the player
      // that have not answered yet. When span navigation is refused while one
      // is pending (the flag turned off, a recording started, a reply began
      // to be spoken as segments), the player's pending start of this clip is
      // cancelled too.
      guardedPlaysInFlight: 0,
      // The request count the newest of those plays was asked at. The
      // cancellation applies only while it is still the newest play() this
      // pill handed to the player: a later press on this pill has already
      // superseded it there, and cancelling by clip would take that press
      // down with it.
      guardedPlayRequest: null,
      // The request count of the newest play() this pill handed to the
      // player. Only a newer play() supersedes a start inside the player; an
      // intent that never reached it (the start of a drag) does not.
      lastServiceRequest: null,
      // Where this pill's clip was, in media seconds, when the player last
      // held it, or null once it finished or before it ever played. A replay
      // after another reply took the player restarts the span it was in.
      lastPosition: null,
    };
  },
  computed: {
    // What the shared playback service needs to stamp clientAudioLagMs: which
    // message just started playing. Null unless both halves are known, so a
    // player mounted without a message identity stamps nothing rather than
    // writing to a path built from undefined.
    lagContext() {
      if (!this.dialogId || !this.messageKey) return null;
      return { dialogId: this.dialogId, messageKey: this.messageKey };
    },
    // The rate this pill plays at, as play()'s fifth argument: the effective
    // rate on a pill with playback controls, and null (1x, not following the
    // chip) on every other pill.
    playRate() {
      return this.playbackControls ? effectivePlaybackRate.value : null;
    },
    // The rate the time labels divide by, so they show the seconds the
    // learner will actually wait. 1 whenever the chip would be hidden.
    labelRate() {
      return this.playbackControls ? effectivePlaybackRate.value : 1;
    },
    // Whether this pill fetches through the shared clip cache, so a phrase tap
    // can play the bytes it holds. Only a pill with playback controls (a tutor
    // message in a lesson or a freestyle chat), and only while phraseReplay is
    // on: every other pill keeps its own fetch and never takes a slot, and
    // turning the flag off stops the shared fetch.
    usesClipCache() {
      return this.playbackControls && clipCacheState.enabled && !!this.audioId;
    },
    showSpeedChip() {
      return this.playbackControls && playbackRateAvailable.value;
    },
    // The spans this pill navigates by, in order, each {start, end, text}.
    // The last span's end is clamped to the player's reported duration,
    // because the stored length comes from decoded audio while the player
    // reports its own.
    spans() {
      const timings = this.spanTimings;
      if (!this.playbackControls || !Array.isArray(timings)) return [];
      const spans = timings
        .filter(
          (timing) =>
            timing &&
            Number.isFinite(timing.start) &&
            Number.isFinite(timing.end) &&
            timing.start >= 0 &&
            timing.end > timing.start
        )
        .map((timing) => ({
          start: timing.start,
          end: timing.end,
          text: typeof timing.text === "string" ? timing.text : "",
        }))
        .sort((a, b) => a.start - b.start);
      const duration = this.displayDuration;
      if (spans.length && duration > 0) {
        const last = spans[spans.length - 1];
        last.end = Math.min(last.end, duration);
        if (last.end <= last.start) spans.pop();
      }
      return spans;
    },
    hasSpans() {
      return this.spans.length > 0;
    },
    // The length a span position is measured against: the player's own
    // duration when known, otherwise the end of the last span.
    spanTimeline() {
      if (this.displayDuration) return this.displayDuration;
      return this.hasSpans ? this.spans[this.spans.length - 1].end : 0;
    },
    // Whether the replay button and a snapped scrub release may start the
    // clip. Not while the flag is off, not while the learner is recording,
    // and not while any reply is being spoken as segments, because a play
    // would end that session and cut the rest of the reply short.
    spanNavigationOpen() {
      return (
        this.hasSpans &&
        clipCacheState.enabled &&
        !recordingState.takeInProgress &&
        !anySegmentSessionLive()
      );
    },
    // The id of the clip the shared player holds, as a computed so a watcher
    // can follow it (playbackState is a module import, not an instance field).
    playerAudioId() {
      return playbackState.currentAudioId;
    },
    lastPositionSource() {
      return this.isCurrent ? playbackState.currentTime : null;
    },
    snapTooltip() {
      if (!this.isScrubbing || this.snapIndex === null) return "";
      const span = this.spans[this.snapIndex];
      return span ? span.text : "";
    },
    // The slider's value as text: the span at the position the slider shows,
    // so the snap is not visual only. Left off a pill without timings.
    valueText() {
      if (!this.hasSpans) return undefined;
      const index =
        this.snapIndex !== null ? this.snapIndex : this.spanIndexAt(this.displayCurrentTime);
      const span = this.spans[index];
      return span && span.text ? span.text : undefined;
    },
    chipRate() {
      return effectivePlaybackRate.value;
    },
    // "0.75×", with the locale's decimal separator but always Latin digits:
    // several supported locales write 0,75, and two would otherwise draw
    // native digits beside the Latin digits of the time label.
    chipLabel() {
      return `${this.formatRate(this.chipRate)}×`;
    },
    isCurrent() {
      return playbackState.currentAudioId === this.audioId;
    },
    isPlaying() {
      return this.isCurrent && playbackState.isPlaying;
    },
    isLoading() {
      return this.playWhenLoaded && !this.audioBlob;
    },
    ariaLabel() {
      if (this.isLoading) return this.$t("audio.loading");
      return this.isPlaying ? this.$t("audio.pause") : this.$t("audio.play");
    },
    // Fixed pseudo-random waveform seeded from the audioId: decorative (no
    // amplitude analysis) but stable per message across re-renders.
    barHeights() {
      let seed = 0;
      const id = this.audioId || "";
      for (let i = 0; i < id.length; i++) {
        seed = (seed * 31 + id.charCodeAt(i)) >>> 0;
      }
      const heights = [];
      for (let i = 0; i < BAR_COUNT; i++) {
        // Math.imul: plain * overflows 2^53 here and degrades the LCG.
        seed = (Math.imul(seed, 1103515245) + 12345) >>> 0;
        heights.push(6 + (seed % 10)); // 6..15px
      }
      return heights;
    },
    displayDuration() {
      if (this.isCurrent && playbackState.duration) return playbackState.duration;
      return this.probedDuration;
    },
    displayCurrentTime() {
      if (this.scrubRatio !== null) return this.scrubRatio * this.displayDuration;
      return this.isCurrent ? playbackState.currentTime : 0;
    },
    playedBars() {
      // Deliberately on the raw ratio rather than displayCurrentTime: going
      // through seconds divides by a duration, which would freeze the preview
      // on a clip whose duration is unreadable — the case the ratio exists for.
      if (this.scrubRatio !== null) return Math.round(this.scrubRatio * BAR_COUNT);
      if (!this.displayDuration || !this.isCurrent) return 0;
      const progress = Math.min(1, this.displayCurrentTime / this.displayDuration);
      return Math.round(progress * BAR_COUNT);
    },
    timeLabel() {
      if (!this.displayDuration) return "";
      // Remaining time while a clip is active; total length at rest. A pending
      // scrub counts as active, or the bars fill to 60% while the label still
      // reads total length.
      if ((this.isCurrent || this.scrubRatio !== null) && this.displayCurrentTime > 0) {
        const remaining = (this.displayDuration - this.displayCurrentTime) / this.labelRate;
        // formatTime rounds, so sub-half-second remainders would read "-0:00".
        if (remaining < 0.5) return this.formatTime(0);
        return "-" + this.formatTime(remaining);
      }
      return this.formatTime(this.displayDuration / this.labelRate);
    },
  },
  methods: {
    // The span a media time falls in: the last one starting at or before it,
    // or the first span for a time before any of them.
    spanIndexAt(time) {
      let found = 0;
      this.spans.forEach((span, index) => {
        if (span.start <= time + SPAN_START_TOLERANCE_SECONDS) found = index;
      });
      return found;
    },
    // The span the replay button restarts: the one the player is in while it
    // holds this clip, and the last span once the reply has finished (the
    // player lets go of a clip that ends naturally).
    replayTargetIndex() {
      if (this.isCurrent && playbackState.duration) {
        return this.spanIndexAt(playbackState.currentTime);
      }
      if (this.lastPosition !== null) return this.spanIndexAt(this.lastPosition);
      return this.spans.length - 1;
    },
    replaySpan() {
      if (!this.spanNavigationOpen) return;
      const now = Date.now();
      let target = null;
      let stepBack = false;
      const last = this.lastReplay;
      if (last && now - last.at < REPLAY_STEP_BACK_WINDOW_MS) {
        const span = this.spans[last.index];
        const spanMs = span ? ((span.end - span.start) / this.labelRate) * 1000 : 0;
        if (now - last.at < spanMs && last.index > 0) {
          target = last.index - 1;
          stepBack = true;
        }
      }
      if (target === null) target = this.replayTargetIndex();
      this.lastReplay = { at: now, index: target };
      // Counted only once the clip actually moves or starts.
      this.playFromSpan(target, () => usePlaybackEvents().trackSpanReplayed({ stepBack }));
    },
    // Moves the clip to a span's start and plays from there: a live seek when
    // the player is sounding this clip, and otherwise the scrub path, which
    // loads the bytes if it must (a paused clip the player holds may have been
    // played by the service without this pill ever fetching it). `onStarted`
    // is called once the clip actually moves or starts.
    playFromSpan(index, onStarted = null) {
      const span = this.spans[index];
      if (!span) return;
      if (this.isCurrent && playbackState.duration && playbackState.isPlaying) {
        this.liveSeek(Math.min(span.start, playbackState.duration));
        if (onStarted) onStarted();
        return;
      }
      const timeline = this.spanTimeline;
      if (!timeline) return;
      this.commitScrub(Math.min(1, span.start / timeline), {
        guarded: true,
        startSeconds: span.start,
        onStarted,
      });
    },
    // A scrub ratio snapped to the start of the span under it, as
    // {ratio, index}, measured against `timeline` seconds.
    snapRatio(ratio, timeline) {
      if (!this.hasSpans || !timeline) return null;
      const index = this.spanIndexAt(ratio * timeline);
      return { ratio: Math.min(1, this.spans[index].start / timeline), index };
    },
    // A seek the learner makes on the clip the player holds. It is their
    // newest request. While the clip is not sounding, a start of this same
    // clip that the player has not answered yet (an earlier replay or scrub)
    // is cancelled, or it would land a moment later at its own older position.
    // Once the clip sounds, a pending start is the one making it sound (on
    // the web the element reports playing before play() resolves), and it
    // already applied its position before starting, so it is left alone.
    //
    // A start of another clip that the player has not received yet is
    // dropped as well, so it cannot take the player from this clip a moment
    // later.
    liveSeek(seconds) {
      this.takePlayIntent();
      this.supersedePendingStarts();
      seek(seconds);
    },
    // Never while a reply is being spoken as segments: its next segment's
    // start is one of those pending starts, and dropping it would cut the
    // reply short.
    supersedePendingStarts() {
      if (!this.audioId || !clipCacheState.enabled || anySegmentSessionLive()) return;
      if (!playbackState.isPlaying) cancelPendingPlaysOf(this.audioId);
      supersedeOtherPendingPlays(this.audioId);
    },
    formatRate(rate) {
      const locale = (this.$i18n && this.$i18n.locale) || "en-US";
      try {
        return new Intl.NumberFormat(locale, {
          numberingSystem: "latn",
          maximumFractionDigits: 2,
        }).format(rate);
      } catch {
        return String(rate);
      }
    },
    cycleSpeed() {
      const fromRate = this.chipRate;
      const toRate = choosePlaybackRate(nextPlaybackRate(playbackRateState.preferred));
      if (toRate === fromRate) return;
      usePlaybackEvents().trackSpeedChanged({ fromRate, toRate });
    },
    formatTime(totalSeconds) {
      if (!isFinite(totalSeconds)) return "0:00";
      const clamped = Math.max(0, Math.round(totalSeconds));
      const minutes = Math.floor(clamped / 60);
      const seconds = clamped % 60;
      return `${minutes}:${String(seconds).padStart(2, "0")}`;
    },
    togglePlay() {
      if (this.isPlaying) {
        // "Stop this clip", not "stay quiet": pausing a reply the learner is
        // reviewing must not silence a different reply that is on its way.
        pause({ silenceUpcomingReplies: false });
        return;
      }
      if (this.audioBlob) {
        // Consume any owed play: this one satisfies it.
        this.playWhenLoaded = false;
        this.takePlayIntent();
        this.lastServiceRequest = this.playIntentRequest;
        play(this.audioId, this.audioBlob, null, this.lagContext, this.playRate);
        return;
      }
      if (this.playWhenLoaded) {
        this.playWhenLoaded = false;
        // Cancelling a pending load strands any scrub waiting on it; rejecting
        // is what routes that exit through commitScrub's finally.
        this.settleBlobDeferred(new Error("Audio load cancelled"));
        return;
      }
      this.playWhenLoaded = true;
      this.takePlayIntent();
      if (!this.hasRequested) this.loadAudio();
    },
    // Records that the learner just asked this pill to play, and returns that
    // request's identity, which an asynchronous play keeps as its own: a later
    // request on this pill or any other replaces the instance fields, and the
    // older play then no longer matches.
    //
    // The request count moves only while phraseReplay is on. With it off,
    // every pill behaves as it did before phrase replay: a press or a step on
    // one pill never cancels a play another pill is still waiting to start.
    takePlayIntent() {
      this.playIntentTaps = sliceState.taps;
      if (clipCacheState.enabled) pillPlayRequests++;
      this.playIntentRequest = pillPlayRequests;
      return { taps: this.playIntentTaps, request: this.playIntentRequest };
    },
    // Whether a play the learner asked for before the bytes arrived may still
    // start: not if they tapped a phrase, or asked any pill to play, since.
    playIntentIsCurrent() {
      return this.playIntentTaps === sliceState.taps && this.playIntentRequest === pillPlayRequests;
    },
    // Resolves once bytes are in hand. Rejects if the fetch fails or the user
    // cancels a pending load, which is what lets commitScrub treat every
    // abandoned path identically instead of clearing state at each exit.
    ensureBlob() {
      // loadAudio bails on a missing id without settling, which would hang the
      // commit forever — a never-settling deferred defeats the finally rather
      // than being caught by it.
      if (!this.audioId) return Promise.reject(new Error("No audioId to load"));
      if (this.audioBlob) return Promise.resolve(this.audioBlob);
      if (!this.blobDeferred) {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
          resolve = res;
          reject = rej;
        });
        this.blobDeferred = { promise, resolve, reject };
      }
      if (!this.hasRequested) this.loadAudio();
      return this.blobDeferred.promise;
    },
    settleBlobDeferred(error) {
      const deferred = this.blobDeferred;
      if (!deferred) return;
      this.blobDeferred = null;
      if (error) deferred.reject(error);
      else deferred.resolve(this.audioBlob);
    },
    // Owns the whole preview → load → play lifecycle so no exit can strand the
    // bars at the scrub position, including exits nobody has thought of yet.
    //
    // A span-navigation play (the replay button, a snapped release, a span
    // keyboard step) passes `guarded`, and it starts only if span navigation
    // is still allowed and was never refused while it waited, so a recording
    // or a reply spoken as segments that began during the download is never
    // cut into. It also passes `startSeconds`, the span's start, which is
    // turned into a ratio of the clip's own duration once that is known,
    // because a ratio of the last span's end would land late on a clip with
    // trailing silence.
    async commitScrub(ratio, { guarded = false, startSeconds = null, onStarted = null } = {}) {
      const commitId = ++this.scrubCommitId;
      const gateClosuresAtStart = this.spanGateClosures;
      const stillAllowed = () =>
        !guarded || (this.spanNavigationOpen && this.spanGateClosures === gateClosuresAtStart);
      this.scrubRatio = ratio;
      const intent = this.takePlayIntent();
      // Nothing was asked of any pill, and no phrase was tapped, since.
      const intentIsCurrent = () =>
        sliceState.taps === intent.taps && pillPlayRequests === intent.request;
      try {
        const blob = await this.ensureBlob();
        // Two scrubs on the same pending load both wake here. Let only the
        // newest reach the player: the older one's load would win the race and
        // seek back to its own position, landing the audio somewhere the bars
        // never showed.
        if (commitId !== this.scrubCommitId) return;
        // A phrase was tapped, or another play was asked for, while the bytes
        // downloaded, so the learner is listening to that now.
        if (!intentIsCurrent()) return;
        if (!stillAllowed()) return;
        let startRatio = ratio;
        if (startSeconds !== null) {
          const duration = await this.waitForDuration();
          if (commitId !== this.scrubCommitId || !intentIsCurrent()) return;
          if (!stillAllowed()) return;
          // The player seeks by a ratio of its own duration, so without that
          // duration any ratio would land somewhere other than the span's
          // start. The play is refused rather than started at a guess.
          if (!duration) return;
          startRatio = Math.min(1, startSeconds / duration);
        }
        // This play satisfies any load that stood down for the scrub.
        this.playWhenLoaded = false;
        let started;
        this.lastServiceRequest = intent.request;
        if (guarded) {
          this.guardedPlaysInFlight++;
          this.guardedPlayRequest = intent.request;
        }
        try {
          started = await play(this.audioId, blob, startRatio, this.lagContext, this.playRate);
        } finally {
          if (guarded) this.guardedPlaysInFlight--;
        }
        // Only a clip the player actually started counts: play() answers
        // false for a refused, failed or superseded start.
        if (started === true && onStarted) onStarted();
      } catch {
        // Load failed or was abandoned; the finally still restores the bars.
      } finally {
        // Only the newest commit may clear: an older one resolving late must
        // not wipe the ratio a newer scrub is currently displaying.
        if (commitId === this.scrubCommitId) this.scrubRatio = null;
      }
    },
    // The clip's duration once the pill knows it: at once when it does,
    // otherwise when the duration probe answers, and 0 if it has not within
    // two seconds. A probe that finished without a duration (a load error) is
    // asked once more, so one failed probe does not leave the replay button
    // doing nothing for the life of the pill.
    waitForDuration() {
      if (this.displayDuration) return Promise.resolve(this.displayDuration);
      return new Promise((resolve) => {
        let stop = null;
        let retried = false;
        const probeIfIdle = () => {
          if (!this.displayDuration && !this.probeCleanup) this.probeDuration();
        };
        const timer = setTimeout(() => {
          if (stop) stop();
          resolve(0);
        }, 2000);
        stop = this.$watch(
          () => [this.displayDuration, !!this.probeCleanup],
          ([duration, probing]) => {
            if (duration) {
              clearTimeout(timer);
              stop();
              resolve(duration);
              return;
            }
            if (!probing && !retried) {
              retried = true;
              probeIfIdle();
            }
          }
        );
        probeIfIdle();
      });
    },
    ratioFromEvent(event) {
      const rect = this.$refs.track.getBoundingClientRect();
      if (!rect.width) return null;
      return Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    },
    seekFromEvent(event) {
      // Captured pointer events can arrive while the component unmounts
      // mid-scrub, after Vue has cleared the ref.
      if (!this.$refs.track) return;
      let ratio = this.ratioFromEvent(event);
      if (ratio === null) return;
      // The fork, not a gate. If the player holds this clip we can seek it
      // live and you hear the audio move under your finger. If it doesn't —
      // including when another clip grabs the player mid-drag — playbackState
      // describes someone else's clip, so preview instead and commit on
      // release.
      const live = this.isCurrent && playbackState.duration;
      // On a pill with timings the position snaps to the start of the span
      // under the finger, so scrubbing becomes reading rather than guessing.
      const snapped = this.snapRatio(ratio, live ? playbackState.duration : this.spanTimeline);
      const previousSnap = this.snapIndex;
      this.snapIndex = snapped ? snapped.index : null;
      // Once a drag has snapped it stays a snapped drag until it ends, even if
      // the timings go away and later moves cannot snap: its release is then
      // dropped rather than played unguarded.
      if (snapped) this.snapStart = this.spans[snapped.index].start;
      if (snapped) ratio = snapped.ratio;
      // A live drag that stays inside one span has nowhere new to go: seeking
      // back to the same start on every move would stutter the audio.
      if (live && snapped && snapped.index === previousSnap) return;
      if (live) {
        // Drop any preview from earlier in this drag: scrubRatio outranks
        // playback for the bars, so a stale one would freeze them where the
        // player took over while the finger moved on.
        this.scrubRatio = null;
        this.supersedePendingStarts();
        seek(playbackState.duration * ratio);
        return;
      }
      this.scrubRatio = ratio;
    },
    onPointerDown(event) {
      // A new gesture supersedes any commit still waiting on a load. Without
      // this the old one lands mid-drag: it plays from its stale position and
      // nulls scrubRatio, so the bars snap away under the finger and the
      // release then has nothing left to commit.
      this.scrubCommitId++;
      // A drag is the learner's newest request: a play still downloading on
      // another pill gives up, the way it does for a press.
      this.takePlayIntent();
      this.isScrubbing = true;
      this.snapIndex = null;
      this.snapStart = null;
      // Capture so the drag keeps scrubbing when the pointer leaves the bars.
      if (this.$refs.track?.setPointerCapture) {
        try {
          this.$refs.track.setPointerCapture(event.pointerId);
        } catch {
          // Pointer may already be inactive (e.g. rapid taps); the scrub
          // still works, it just loses tracking outside the bars.
        }
      }
      this.seekFromEvent(event);
    },
    onPointerMove(event) {
      if (!this.isScrubbing) return;
      this.seekFromEvent(event);
    },
    endDrag(event) {
      this.isScrubbing = false;
      if (this.$refs.track?.releasePointerCapture) {
        try {
          this.$refs.track.releasePointerCapture(event.pointerId);
        } catch {
          // Capture may already be released (e.g. pointercancel); ignore.
        }
      }
    },
    // The browser took the gesture away (interrupted touch, scroll takeover).
    // The user never released, so drop the preview rather than committing a
    // seek and starting playback they did not ask for.
    onPointerCancel(event) {
      if (!this.isScrubbing) return;
      this.endDrag(event);
      this.scrubRatio = null;
      this.snapIndex = null;
      this.snapStart = null;
      // A load that finished mid-drag stood down for the scrub instead of
      // racing it. The gesture is gone now, so the play it was owed still
      // stands and would otherwise be lost silently.
      if (this.playWhenLoaded && this.audioBlob) {
        this.playWhenLoaded = false;
        if (!this.playIntentIsCurrent()) return;
        this.lastServiceRequest = this.playIntentRequest;
        play(this.audioId, this.audioBlob, null, this.lagContext, this.playRate);
      }
    },
    onPointerUp(event) {
      if (!this.isScrubbing) return;
      this.endDrag(event);
      const snapStart = this.snapStart;
      this.snapIndex = null;
      this.snapStart = null;
      if (snapStart !== null) {
        this.releaseSnapped(snapStart);
        return;
      }
      // Set only on the preview path; a live seek has already moved the audio.
      if (this.scrubRatio !== null) {
        this.commitScrub(this.scrubRatio);
        return;
      }
      // Live-seek path. A scrub always ends in playback whatever the clip's
      // prior state, so a paused clip resumes from where it was dragged to
      // rather than sitting silently at the new position. null, not a ratio:
      // the seek above already moved it, and 0 would restart from the top.
      if (this.isCurrent && !playbackState.isPlaying && this.audioBlob) {
        this.takePlayIntent();
        this.lastServiceRequest = this.playIntentRequest;
        play(this.audioId, this.audioBlob, null, this.lagContext, this.playRate);
      }
    },
    // The release of a drag that snapped to the span starting at `start`: it
    // plays from that start. It is inert whenever the replay button is, and
    // when the timings went away during the drag (the flag turned off): the
    // bars go back to rest and nothing plays.
    releaseSnapped(start) {
      if (!this.hasSpans || !this.spanNavigationOpen) {
        this.scrubRatio = null;
        return;
      }
      // Counted only once the release actually moves or starts the clip.
      const countSnap = () => usePlaybackEvents().trackScrubSnapped();
      const held = this.isCurrent && playbackState.duration;
      if (held && playbackState.isPlaying) {
        // A live drag seeks once per span, and the clip kept playing while the
        // finger rested, so the release puts it back on the snapped start.
        this.scrubRatio = null;
        this.liveSeek(Math.min(start, playbackState.duration));
        countSnap();
        return;
      }
      // Paused or not held: the loading path, which fetches the bytes when
      // this pill never did (the service may have played the stitched file
      // itself) and starts the clip at the span.
      const timeline = held ? playbackState.duration : this.spanTimeline;
      let ratio = this.scrubRatio;
      if (ratio === null && timeline) ratio = Math.min(1, start / timeline);
      if (ratio === null) return;
      this.commitScrub(ratio, { guarded: true, startSeconds: start, onStarted: countSnap });
    },
    // WAI-ARIA slider pattern: arrows step, Home/End jump to the extremes.
    // With span timings the arrows step by span instead of five seconds.
    onKeydown(event) {
      if (this.hasSpans && ["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) {
        this.stepBySpan(event);
        return;
      }
      const step = (from, limit) => {
        if (event.key === "ArrowRight") return from + KEYBOARD_SEEK_STEP_SECONDS;
        if (event.key === "ArrowLeft") return from - KEYBOARD_SEEK_STEP_SECONDS;
        if (event.key === "Home") return 0;
        if (event.key === "End") return limit;
        return null;
      };
      // Same fork as the pointer, and for the same reason: this handler reaches
      // the shared player, so acting on playbackState without establishing that
      // the player holds *this* clip would move a different clip's audio.
      if (this.isCurrent && playbackState.duration) {
        const target = step(playbackState.currentTime, playbackState.duration);
        if (target === null) return;
        event.preventDefault();
        // A step on the clip the player holds is the learner's newest request.
        this.liveSeek(Math.max(0, Math.min(playbackState.duration, target)));
        return;
      }
      // A keystroke carries no intrinsic position, so this branch needs a real
      // timeline of its own. With none, the keys do nothing — a pointer drag on
      // the same clip still works, since its ratio comes from the gesture.
      const duration = this.displayDuration;
      if (!duration) {
        // A fetch-deferred player has loaded nothing, so it has no timeline at
        // all and the keyboard path would stay inert for the life of the
        // message. Load on the first seek key so the next one has a timeline to
        // step along; a pointer scrub already loads through commitScrub.
        if (this.deferFetch && !this.hasRequested && step(0, 0) !== null) {
          event.preventDefault();
          this.loadAudio();
        }
        return;
      }
      const base = this.scrubRatio === null ? 0 : this.scrubRatio * duration;
      const target = step(base, duration);
      if (target === null) return;
      event.preventDefault();
      this.commitScrub(Math.max(0, Math.min(duration, target)) / duration);
    },
    // An arrow key on a pill with timings: the next or the previous span's
    // start; Home and End: the first and the last span's start. A live seek
    // when the player holds this clip; otherwise it plays
    // from there, which is inert whenever the replay button is.
    stepBySpan(event) {
      const live = this.isCurrent && playbackState.duration;
      const timeline = live ? playbackState.duration : this.spanTimeline;
      if (!timeline) return;
      event.preventDefault();
      // Where the pill is: the player's clock while it holds this clip, a
      // pending scrub's position, or where the clip was when another reply
      // took the player (the replay button reads the same position).
      let position = this.lastPosition !== null ? this.lastPosition : 0;
      if (live) position = playbackState.currentTime;
      else if (this.scrubRatio !== null) position = this.scrubRatio * timeline;
      const last = this.spans.length - 1;
      let index;
      if (event.key === "Home") index = 0;
      else if (event.key === "End") index = last;
      else {
        const step = event.key === "ArrowRight" ? 1 : -1;
        index = Math.max(0, Math.min(last, this.spanIndexAt(position) + step));
      }
      const start = this.spans[index].start;
      if (live) {
        // A step on the clip the player holds is the learner's newest request.
        this.liveSeek(Math.min(start, playbackState.duration));
        return;
      }
      if (!this.spanNavigationOpen) return;
      this.commitScrub(Math.min(1, start / timeline), { guarded: true, startSeconds: start });
    },
    probeDuration() {
      if (!this.audioBlob || this.probedDuration) return;
      const url = URL.createObjectURL(this.audioBlob);
      const probe = new Audio();
      probe.preload = "metadata";
      // Fully release the element: iOS WKWebView caps concurrent media
      // decoders, and a chat renders one probe per audio message, so a probe
      // that keeps its src pinned can starve real playback.
      const cleanup = () => {
        this.probeCleanup = null;
        probe.onloadedmetadata = null;
        probe.onerror = null;
        probe.removeAttribute("src");
        probe.load();
        URL.revokeObjectURL(url);
      };
      this.probeCleanup = cleanup;
      probe.onloadedmetadata = () => {
        if (isFinite(probe.duration)) this.probedDuration = probe.duration;
        cleanup();
      };
      probe.onerror = cleanup;
      probe.src = url;
    },
    // Hands this pill's bytes to the shared clip cache while the pill uses it,
    // unless the cache was cleared (a dialog switch, another account) after
    // the bytes were requested.
    handBlobToCache() {
      if (!this.usesClipCache || !this.audioBlob) return;
      if (this.blobCacheGeneration !== clipCacheGeneration()) return;
      // Only under the id the bytes were fetched for: a pill whose audioId
      // changed still holds the previous clip's bytes.
      if (this.blobAudioId !== this.audioId) return;
      adoptClip(this.audioId, toRaw(this.audioBlob));
    },
    // Holds this pill's current clip in the shared cache while it uses the
    // cache, and releases a clip it no longer should hold.
    syncClipHold(shouldHold) {
      const wanted = shouldHold ? this.audioId : null;
      if (this.heldClipId === wanted) return;
      const previous = this.heldClipId;
      this.heldClipId = wanted;
      if (wanted) {
        holdClip(wanted);
        // A pill that fetched through its own path before the flag turned on
        // hands its bytes over, so a phrase tap never fetches them again.
        this.handBlobToCache();
      }
      if (previous) releaseClip(previous);
    },
    // The pill's own copy of its clip, fetched the way it always has been.
    async fetchOwnClip(signal) {
      const params = new URLSearchParams({ audioId: this.audioId });
      const url = cloudFunctions.getAudioApi() + "?" + params;
      const token = await authStore.getIdToken();
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + token,
        },
        priority: this.isAutoPlay ? "high" : "low",
        signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    },
    async loadAudio() {
      if (this.hasRequested || !this.audioId) return;
      this.hasRequested = true;
      this.abortController = new AbortController();
      const cacheGenerationAtRequest = clipCacheGeneration();
      const requestedAudioId = this.audioId;
      try {
        // Through the shared cache the request joins any fetch of the same
        // clip already in flight (the service's prefetch of a stitched clip),
        // and aborting this pill's signal gives up only this pill's wait.
        this.audioBlob = this.usesClipCache
          ? await getClip(this.audioId, {
            priority: this.isAutoPlay ? "high" : "low",
            signal: this.abortController.signal,
          })
          : await this.fetchOwnClip(this.abortController.signal);
        this.blobCacheGeneration = cacheGenerationAtRequest;
        this.blobAudioId = requestedAudioId;
        // phraseReplay may have turned on while the pill's own fetch was in
        // flight; the watcher found no blob to hand over then, so hand it over
        // now. The cache keeps a clip it already holds.
        this.handBlobToCache();
        this.probeDuration();
        this.settleBlobDeferred();
        // A pending scrub owns the play and knows the position, so don't race
        // it from zero. Leave playWhenLoaded set rather than consuming it: the
        // request is still owed, and a cancelled gesture has to honor it.
        if (this.playWhenLoaded && this.scrubRatio === null) {
          this.playWhenLoaded = false;
          // A phrase tapped while these bytes downloaded wins.
          if (!this.playIntentIsCurrent()) return;
          this.$nextTick(() => {
            this.lastServiceRequest = this.playIntentRequest;
            play(this.audioId, this.audioBlob, null, this.lagContext, this.playRate);
          });
        }
      } catch (error) {
        this.settleBlobDeferred(error);
        if (error.name === "AbortError") return;
        this.hasRequested = false;
        this.playWhenLoaded = false;
        console.error(`Audio fetch error for audioId=${this.audioId}:`, error);
      }
    },
  },
  mounted() {
    if (this.isAutoPlay) {
      this.loadAudio();
      return;
    }
    // togglePlay, ensureBlob and commitScrub all load on demand, so deferring
    // costs the learner nothing but a first-press wait they asked for.
    if (this.deferFetch) return;
    if (typeof IntersectionObserver === "undefined") {
      this.loadAudio();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadAudio();
            this.observer.disconnect();
            this.observer = null;
            break;
          }
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );
    this.observer.observe(this.$el);
  },
  beforeUnmount() {
    // A scrub or span-navigation play still waiting for bytes or for the
    // clip's duration must never start once the pill is gone, and neither
    // may one this pill already handed to the player that has not started
    // sounding yet.
    this.scrubCommitId++;
    if (this.audioId) cancelPendingPlaysOf(this.audioId);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.probeCleanup) {
      this.probeCleanup();
    }
    this.syncClipHold(false);
  },
  watch: {
    // Synchronous, so a refusal that lasts only a moment (a reply spoken as
    // segments that starts and ends while bytes download) is still counted.
    spanNavigationOpen: {
      handler(open, wasOpen) {
        if (!wasOpen || open) return;
        this.spanGateClosures++;
        // A span play already handed to the player must not start either.
        // Only this clip's starts that have not begun sounding are cancelled.
        if (
          this.guardedPlaysInFlight > 0 &&
          this.guardedPlayRequest === this.lastServiceRequest &&
          this.audioId
        ) {
          cancelPendingPlaysOf(this.audioId);
        }
      },
      flush: "sync",
    },
    // Where the clip is while the player holds it, for the replay button.
    lastPositionSource: {
      handler(position) {
        if (position !== null) this.lastPosition = position;
      },
      immediate: true,
    },
    // The player let go of this clip: at its natural end (the id is cleared),
    // which counts as finished, or to another clip, which keeps the position.
    // Only a release at the clip's end counts as finished: the service moves
    // the clock to the end before it clears the id then.
    playerAudioId(next, previous) {
      if (previous !== this.audioId || next !== null) return;
      const duration = playbackState.duration;
      if (duration > 0 && playbackState.currentTime >= duration - NATURAL_END_TOLERANCE_SECONDS) {
        this.lastPosition = null;
      }
    },
    // Immediate, so a pill mounted while phraseReplay is on holds its clip
    // from the start, and a watcher rather than a mount-time read because the
    // flag can flip in either direction while the chat is open.
    usesClipCache: {
      handler(uses) {
        this.syncClipHold(uses);
      },
      immediate: true,
    },
    audioId() {
      this.syncClipHold(this.usesClipCache);
    },
  },
};
</script>

<style scoped>
.chat-audio {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  background-color: var(--al-surface-raised);
  border: 1px solid var(--al-border);
  border-radius: 999px;
  min-height: 44px;
  padding: 8px 14px 8px 9px;
  margin-top: 10px;
  max-width: 100%;
  user-select: none;
}

.play-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: var(--al-primary);
  color: #fff;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  flex-shrink: 0;
  transition: background-color var(--al-dur-short) var(--al-ease-move);
}

.play-btn:hover {
  background-color: var(--al-primary-hover);
}

.play-btn[aria-busy="true"] {
  cursor: progress;
}

/* The waveform is the seek surface: tap to jump, drag to scrub. touch-action
   none keeps a scrub from also scrolling the chat on mobile. */
.waveform {
  display: flex;
  align-items: center;
  gap: 2.5px;
  height: 24px;
  cursor: pointer;
  touch-action: none;
}

.waveform:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 4px;
  border-radius: 4px;
}

/* WCAG 1.4.11-conscious: unplayed bars are a muted text mix so they stay
   visible on the raised pill in both themes; played bars flip to primary. */
.bar {
  width: 2.5px;
  border-radius: 2px;
  background-color: color-mix(in srgb, var(--al-text-muted) 45%, transparent);
  flex-shrink: 0;
  transition: background-color 120ms var(--al-ease-move);
}

.bar.played {
  background-color: var(--al-primary);
}

.time {
  font-size: 12px;
  color: var(--al-text-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

/* The speed chip: a small pill after the time label. It draws at 24px so the
   audio pill keeps its height, and its 44px hit area comes from the ::before
   layer, which is centred on it and invisible. Neutral at 1x; the primary-soft
   fill marks a slowed speed, the way DESIGN.md marks a selected chip. The label
   stays --al-text on both fills for 4.5:1 at 12px. */
.speed-chip {
  position: relative;
  flex-shrink: 0;
  min-width: 40px;
  height: 24px;
  padding: 0 8px;
  border-radius: var(--al-radius-pill, 999px);
  border: 1px solid var(--al-border);
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    border-color var(--al-dur-short) var(--al-ease-move);
}

.speed-chip::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: max(100%, 44px);
  height: 44px;
  transform: translate(-50%, -50%);
}

.speed-chip-slowed {
  background-color: var(--al-primary-soft);
  border-color: color-mix(in srgb, var(--al-primary) 35%, transparent);
}

@media (hover: hover) and (pointer: fine) {
  .speed-chip:hover {
    background-color: var(--al-primary-soft-hover);
  }
}

.speed-chip:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .speed-chip {
    transition: none;
  }
}

/* The replay button: a 28px round glyph between the time label and the speed
   chip, with a 44px target from the invisible ::before layer. Muted at rest,
   like the time label beside it. */
.replay-btn {
  position: relative;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: var(--al-text-muted);
  cursor: pointer;
  transition: color var(--al-dur-short) var(--al-ease-move),
    background-color var(--al-dur-short) var(--al-ease-move);
}

.replay-btn::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 44px;
  height: 44px;
  transform: translate(-50%, -50%);
}

.replay-btn svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.25;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@media (hover: hover) and (pointer: fine) {
  .replay-btn:not(:disabled):hover {
    color: var(--al-text);
    background-color: var(--al-surface-sunken);
  }
}

.replay-btn:focus-visible {
  outline: 2px solid var(--al-primary);
  outline-offset: 2px;
}

.replay-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

/* Below 290px of message width the pill cannot hold the replay button beside
   the chip, so it is hidden; the snapping scrub and the keyboard still step by
   span. The container is TutorMessage's audio row. */
@container (max-width: 289px) {
  .replay-btn {
    display: none;
  }
}

/* The span a snapped scrub would play from, above the pill. Capped to the
   pill's width so it never widens the page at 360px. */
.scrub-tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  max-width: 100%;
  padding: 4px 10px;
  border: 1px solid var(--al-border);
  border-radius: 8px;
  background-color: var(--al-surface-raised);
  box-shadow: var(--al-shadow-sm);
  color: var(--al-text);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  z-index: 2;
}

@media (prefers-reduced-motion: reduce) {
  .replay-btn {
    transition: none;
  }
}
</style>
